import type { MyCustomer } from '../api/types';
import { parseFactTarget } from '../customer/shell/factTarget';
import {
  MEDSPA_SECTIONS as MEDSPA_REGISTRY,
  sectionVisible,
  TELEHEALTH_SECTIONS as TELEHEALTH_REGISTRY,
} from '../customer/sections/registry';

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const TARGET = new RegExp(`^/(portal|c|customers)/(${UUID})$`, 'i');
// The sections a link may open are the ONE section registry's (PM-7): a second
// hand-kept list here lacked Licences and deadlines, Changes in the law and
// the email settings, so every owner-summary link to them fell back to the
// home page after sign-in. Who may open each is still `sectionVisible`.
const TELEHEALTH_SECTIONS = new Set(TELEHEALTH_REGISTRY.map((entry) => entry.id));
const MEDSPA_SECTIONS = new Set(MEDSPA_REGISTRY.map((entry) => entry.id));

/** Only a known route supported by the server's live capacities. */
export function authorizedReturnTarget(
  path: string | null,
  grants: MyCustomer[],
  isTeam: boolean,
): string | null {
  if (!path) return null;
  if (!path.startsWith('/') || path.startsWith('//')) return null;
  const url = new URL(path, 'https://return-to.invalid');
  if (url.origin !== 'https://return-to.invalid') return null;
  // Shared reference reads need any live workspace capacity, not a customer
  // selected in the URL. Keep only the lookup's bounded selection parameters.
  if (url.pathname === '/scope') {
    if ((!isTeam && grants.length === 0) || url.hash) return null;
    const patterns = new Map<string, RegExp>([
      ['state', /^[A-Z]{2}$/],
      ['role', /^[^\u0000-\u001f\u007f]{1,40}$/],
      ['service', /^[a-z0-9_]{1,80}$/],
    ]);
    for (const [key, value] of url.searchParams) {
      if (url.searchParams.getAll(key).length !== 1 || !patterns.get(key)?.test(value)) return null;
    }
    return path;
  }
  const match = TARGET.exec(url.pathname);
  if (!match) return null;
  const [, surface, customerId] = match;
  const rows = grants.filter((row) => row.customer_id.toLowerCase() === customerId.toLowerCase());
  if (!rows.length) return null;
  const capacities = new Set(rows.flatMap((row) => row.capacities ?? [row.my_role]));
  if (surface === 'customers' && !isTeam) return null;
  if (surface === 'c' && !isTeam && !capacities.has('attorney')) return null;
  if (surface === 'portal' && !isTeam &&
      !capacities.has('customer') && !capacities.has('customer_admin') &&
      !capacities.has('customer_records') && !capacities.has('customer_readonly')) return null;
  // A records-only person is carried only to a section they may open.
  const recordsOnly = !isTeam && capacities.has('customer_records') &&
    !capacities.has('customer') && !capacities.has('customer_admin');
  if (surface === 'portal') {
    const allowed = rows[0]?.vertical === 'telehealth_rx' ? TELEHEALTH_SECTIONS : MEDSPA_SECTIONS;
    // One allow-list of query keys, each at most once: a reminder item (COUNTS) and an
    // invitation's licence question (PARTIALS) are the only typed targets, each on its own section.
    const KEYS = ['s', 'target', 'question', 'itemId'];
    if ([...url.searchParams.keys()].some((key) => !KEYS.includes(key))) return null;
    if (KEYS.some((key) => url.searchParams.getAll(key).length > 1)) return null;
    const section = url.searchParams.get('s');
    if (section && !allowed.has(section)) return null;
    if (section && !sectionVisible(rows[0]?.vertical === 'telehealth_rx' ? 'telehealth' : 'medspa', section, recordsOnly)) return null;
    const itemId = url.searchParams.get('itemId');
    if (url.searchParams.has('itemId') && (!itemId || !/^[0-9a-f]{24}$/.test(itemId))) return null;
    if (url.searchParams.has('target')) {
      const rawTarget = url.searchParams.get('target');
      if (rows[0]?.vertical !== 'telehealth_rx' || !rawTarget || rawTarget.length > 1200) return null;
      if (section === 'need') {
        // A reminder item's typed destination (COUNTS).
        try {
          const target: unknown = JSON.parse(rawTarget);
          if (!target || typeof target !== 'object' || Array.isArray(target)) return null;
          const t = target as Record<string, unknown>;
          if (!['person', 'state', 'medication', 'care_process'].includes(String(t.kind)) ||
              Object.keys(t).some((key) => !['kind', 'personId', 'state', 'medication', 'field', 'itemId'].includes(key)) ||
              Object.entries(t).some(([key, value]) => key !== 'kind' && (typeof value !== 'string' || value.length > 160))) return null;
          if (t.itemId !== undefined && !/^[0-9a-f]{24}$/.test(String(t.itemId))) return null;
        } catch { return null; }
      } else if (section === 'prescribers') {
        // An invitation's licence question (PARTIALS).
        const item = parseFactTarget(rawTarget);
        if (!item || item.kind !== 'person' || !item.personId ||
            !new RegExp(`^${UUID}$`, 'i').test(item.personId) ||
            !/^[A-Z]{2}$/.test(item.state ?? '') ||
            !['provider.state_license', 'provider.dea_registration', 'provider.state_csr',
              'provider.prescriptive_authority',
              'provider.out_of_state_telehealth_registration'].includes(item.field ?? '') ||
            item.medication) return null;
      } else {
        return null;
      }
    }
    const question = url.searchParams.get('question');
    if (question && (rows[0]?.vertical !== 'medspa_service' || section !== 'questions' || question.length > 300 || !/^[A-Za-z0-9_:#.-]+$/.test(question))) return null;
    if (section === 'access' && !isTeam && !capacities.has('customer_admin')) return null;
  } else if (url.search) {
    return null;
  }
  // The fragment is an in-page item anchor. The browser never sends it to the API.
  if (url.hash && !/^#[A-Za-z0-9_-]{1,100}$/.test(url.hash)) return null;
  return path;
}
